import {AxiosError} from 'axios';
import {useMutation, UseMutationOptions} from '@tanstack/react-query';

import {api} from '@eksab/api';
import {useInvalidateMe} from '@eksab/hooks/useMeQuery';
import {useAccessToken} from '@eksab/hooks/useAccessToken';
import {prefetchImage} from '@eksab/util';

import {useGuestStore} from './useGuestStore';
import {useTriviaStore} from '../state';
import type {Question} from '../types';

type JoinChallengeResponse = {
  attempt_id: string;
  first_question: Question;
};

export const useJoinChallengeMutation = (
  challengeID: string,
  options?: UseMutationOptions<JoinChallengeResponse, AxiosError>,
) => {
  const invalidateMe = useInvalidateMe();
  const accessTokenQuery = useAccessToken();

  const isLoggedIn = accessTokenQuery.isSuccess;

  return useMutation<JoinChallengeResponse, AxiosError>(
    () =>
      api
        .post<{data: JoinChallengeResponse}>(`v1/challenges/${challengeID}/join`, undefined, {
          headers: {
            'X-Locale': useTriviaStore.getState().language!,
            Authorization: `Bearer ${isLoggedIn ? accessTokenQuery.data : useGuestStore.getState().token}`,
          },
        })
        .then((res) => res.data.data),
    {
      ...options,
      onSuccess: (...params) => {
        if (isLoggedIn) {
          invalidateMe();
        }

        const firstQuestion = params[0].first_question;

        if (firstQuestion.type === 'image') prefetchImage(firstQuestion.image);
        if (firstQuestion.answers_type === 'image')
          firstQuestion.answers.forEach(({media_url}) => prefetchImage(media_url));

        options?.onSuccess?.(...params);
      },
    },
  );
};
